import { useCallback, useEffect, useState } from 'react';
import AutomaticTexts from './AutomaticTexts';
import { createMessageTemplate, deleteMessageTemplate, getLocations, getMessageTemplates, updateMessageTemplate } from '../services/api.js';
const MessageTemplates = ({ authToken }) => {
  const [locations, setLocations] = useState([]);
  const [messagingEnabled, setMessagingEnabled] = useState(true); // Default to true
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    template_type: 'general',
    content: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const fetchTemplates = useCallback(async () => {
    console.log('MessageTemplates: fetchTemplates called');
    try {
      setLoading(true);
      console.log('MessageTemplates: Making API call to getMessageTemplates');
      const response = await getMessageTemplates();
      console.log('MessageTemplates: API response received', response);
      const templateList = response.results || response || [];
      setTemplates(templateList);
      setError(null);
      setSuccessMessage(null);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setError(err.response?.data?.detail || 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter and search templates
  useEffect(() => {
    let filtered = templates;
    
    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(template => template.template_type === typeFilter);
    }
    
    // Search by name or content
    if (searchTerm) {
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredTemplates(filtered);
  }, [templates, searchTerm, typeFilter]);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Template name is required';
    } else if (formData.name.length > 100) {
      errors.name = 'Template name must be less than 100 characters';
    }
    
    if (!formData.content.trim()) {
      errors.content = 'Template content is required';
    }

    // Prevent creation of appointment templates (but allow editing existing ones)
    if (!editingTemplate && formData.template_type !== 'general') {
      errors.template_type = 'Only general message templates can be created. Appointment templates already exist and can be edited.';
    }
    
    // Check for duplicate names within the same type
    const isDuplicate = templates.some(template =>
      template.name.toLowerCase() === formData.name.toLowerCase() &&
      template.template_type === formData.template_type &&
      template.id !== editingTemplate?.id
    );
    
    if (isDuplicate) {
      errors.name = 'A template with this name already exists';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    console.log('MessageTemplates: useEffect triggered - loading templates');
    fetchTemplates();
  }, [fetchTemplates]);

  // Add a retry mechanism if templates are empty after initial load
  useEffect(() => {
    if (!loading && templates.length === 0 && !error) {
      const retryTimer = setTimeout(() => {
        fetchTemplates();
      }, 500);
      return () => clearTimeout(retryTimer);
    }
  }, [loading, templates.length, error, fetchTemplates]);

  // Fetch locations to check messaging enabled status
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const locationsData = await getLocations();
        setLocations(locationsData);
        // Check if any location has messaging enabled
        const hasMessagingEnabled = locationsData.some(location => location.messaging_enabled);
        setMessagingEnabled(hasMessagingEnabled);
      } catch (error) {
        console.error("Error fetching locations:", error);
        // Default to true if we can't fetch locations
        setMessagingEnabled(true);
      }
    };

    fetchLocations();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      if (editingTemplate) {
        await updateMessageTemplate(authToken, editingTemplate.id, formData);
        setSuccessMessage('Template updated successfully!');
      } else {
        await createMessageTemplate(authToken, formData);
        setSuccessMessage('Template created successfully!');
      }
      
      setFormData({ name: '', template_type: 'general', content: '' });
      setFormErrors({});
      setEditingTemplate(null);
      setShowCreateForm(false);
      fetchTemplates();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save template:', err);
      if (err.response?.data?.name) {
        setFormErrors({ name: err.response.data.name[0] });
      } else if (err.response?.data?.content) {
        setFormErrors({ content: err.response.data.content[0] });
      } else {
        setError(err.response?.data?.detail || 'Failed to save template');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      template_type: template.template_type || 'general',
      content: template.content
    });
    setFormErrors({});
    setError(null);
    setShowCreateForm(true);
  };

  const handleDelete = async (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (window.confirm(`Are you sure you want to delete the template "${template?.name}"? This action cannot be undone.`)) {
      try {
        setSaving(true);
        await deleteMessageTemplate(authToken, templateId);
        setSuccessMessage('Template deleted successfully!');
        fetchTemplates();
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err) {
        console.error('Failed to delete template:', err);
        setError(err.response?.data?.detail || 'Failed to delete template');
      } finally {
        setSaving(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', template_type: 'general', content: '' });
    setFormErrors({});
    setEditingTemplate(null);
    setShowCreateForm(false);
    setError(null);
  };

  const getVariableHelp = () => {
    return [
      '{{first_name}} - Lead\'s first name',
      '{{last_name}} - Lead\'s last name', 
      '{{full_name}} - Lead\'s full name',
      '{{phone}} - Lead\'s phone number',
      '{{email}} - Lead\'s email address',
      '{{booking_url}} - Your online booking link (set in the Info menu)'
    ];
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 pt-24">
      <AutomaticTexts />

      {/* Messaging Disabled Warning */}
      {!messagingEnabled && (
        <div className="bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded-md mb-6 flex items-center">
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <div>
            <p className="font-medium">Messaging Feature Disabled</p>
            <p className="text-sm mt-1">
              Message templates are currently disabled for your location. Please
              contact your marketing company to enable this feature.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Message Templates
          </h1>
          <p className="text-gray-600 mt-1">
            Create and manage message templates for SMS and email communications
          </p>
          <p className="text-sm text-gray-500 mt-1">
            💡 For appointment-specific templates, use the separate Appointment
            Templates page
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          disabled={saving || !messagingEnabled}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-primary focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Create Template
        </button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md mb-4 flex items-center">
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4 flex items-center">
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {error}
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Templates
            </label>
            <input
              type="text"
              placeholder="Search by name or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="general">General Messages</option>
              <option value="appointment_reminder">
                Appointment Reminders (View Only)
              </option>
              <option value="appointment_confirmation">
                Appointment Confirmations (View Only)
              </option>
            </select>
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              Showing {filteredTemplates.length} of {templates.length} templates
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-sm border border-border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {editingTemplate ? "Edit Template" : "Create New Template"}
            </h2>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Form Fields */}
              <div className="lg:col-span-2 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                      formErrors.name
                        ? "border-red-300 focus:border-red-500"
                        : "border-border focus:border-blue-500"
                    }`}
                    placeholder="e.g., Appointment Reminder"
                  />
                  {formErrors.name && (
                    <p className="text-red-600 text-sm mt-1">
                      {formErrors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Type *
                  </label>
                  <select
                    value={formData.template_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        template_type: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={
                      editingTemplate
                        ? true
                        : formData.template_type !== "general"
                    }
                  >
                    {editingTemplate ? (
                      <>
                        <option value="general">General Message</option>
                        <option value="appointment_reminder">
                          Appointment Reminder
                        </option>
                        <option value="appointment_confirmation">
                          Appointment Confirmation
                        </option>
                      </>
                    ) : (
                      <option value="general">General Message</option>
                    )}
                  </select>
                  {!editingTemplate && (
                    <p className="text-sm text-gray-500 mt-1">
                      💡 Only general message templates can be created.
                      Appointment templates already exist and can be edited.
                    </p>
                  )}
                  {editingTemplate &&
                    editingTemplate.template_type !== "general" && (
                      <p className="text-sm text-blue-600 mt-1">
                        ✏️ Editing existing appointment template - type cannot
                        be changed
                      </p>
                    )}
                  {formErrors.template_type && (
                    <p className="text-red-600 text-sm mt-1">
                      {formErrors.template_type}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Content *
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) =>
                      setFormData({ ...formData, content: e.target.value })
                    }
                    rows="6"
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                      formErrors.content
                        ? "border-red-300 focus:border-red-500"
                        : "border-border focus:border-blue-500"
                    }`}
                    placeholder="Hi {{first_name}}, this is a reminder about your upcoming appointment..."
                  />
                  {formErrors.content && (
                    <p className="text-red-600 text-sm mt-1">
                      {formErrors.content}
                    </p>
                  )}
                  <p className="text-sm text-gray-500 mt-2">
                    {formData.content.length}/1000 characters
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-primary focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {saving && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    )}
                    {editingTemplate ? "Update Template" : "Create Template"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={saving}
                    className="bg-accent-teal-subtle text-gray-700 px-6 py-2 rounded-md hover:bg-background focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Variable Help */}
              <div className="bg-background rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Available Variables
                </h3>
                <div className="space-y-2">
                  {getVariableHelp().map((variable, index) => (
                    <div
                      key={index}
                      className="text-xs text-gray-600 font-mono bg-white px-2 py-1 rounded"
                    >
                      {variable}
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-accent-teal-light-bg rounded-md">
                  <p className="text-xs text-blue-700">
                    💡 <strong>Tip:</strong> Variables will be automatically
                    replaced with actual lead data when sending messages.
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Templates List */}
      <div className="space-y-4">
        {filteredTemplates.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-border">
            {templates.length === 0 ? (
              <>
                <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No templates yet
                </h3>
                <p className="text-gray-600 mb-4">
                  Create your first message template to get started
                </p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  disabled={!messagingEnabled}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-primary focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Your First Template
                </button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No templates found
                </h3>
                <p className="text-gray-600">
                  Try adjusting your search or filter criteria
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-white border border-border rounded-lg p-6 hover:shadow-sm transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {template.name}
                      </h3>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          template.template_type === "appointment_reminder"
                            ? "bg-accent-teal-subtle text-blue-800"
                            : template.template_type ===
                                "appointment_confirmation"
                              ? "bg-green-100 text-green-800"
                              : "bg-purple-100 text-purple-800"
                        }`}
                      >
                        {template.template_type === "appointment_reminder"
                          ? "Appointment Reminder"
                          : template.template_type ===
                              "appointment_confirmation"
                            ? "Appointment Confirmation"
                            : "General Message"}
                      </span>
                      {!template.is_active && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-teal-subtle text-gray-800">
                          Inactive
                        </span>
                      )}
                    </div>

                    <div className="bg-background rounded-md p-3 mb-3">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {template.content}
                      </p>
                    </div>

                    <div className="flex items-center text-xs text-gray-500 space-x-4">
                      <span>
                        Created:{" "}
                        {new Date(template.created_at).toLocaleDateString()}
                      </span>
                      {template.updated_at !== template.created_at && (
                        <span>
                          Updated:{" "}
                          {new Date(template.updated_at).toLocaleDateString()}
                        </span>
                      )}
                      <span>
                        By: {template.created_by?.username || "Unknown"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(template)}
                      disabled={saving || !messagingEnabled}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-accent-teal-light-bg rounded-md transition-colors disabled:opacity-50"
                      title={
                        template.template_type !== "general"
                          ? "Edit this appointment template"
                          : "Edit this message template"
                      }
                    >
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit
                    </button>
                    {template.template_type === "general" ? (
                      <button
                        onClick={() => handleDelete(template.id)}
                        disabled={saving || !messagingEnabled}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                      >
                        <svg
                          className="w-4 h-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        Delete
                      </button>
                    ) : (
                      <div
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-400 cursor-not-allowed"
                        title="Appointment templates cannot be deleted"
                      >
                        <svg
                          className="w-4 h-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m0 0v2m0-2h2m-2 0h-2m9-6a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Protected
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageTemplates;